import { defineStore } from "pinia";
import { ref } from "vue";
import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import { createAuthFetch, currentCleanUri, hasOidcCallbackParams } from "@core/auth";

const shouldRegisterSigninRedirectCallback = hasOidcCallbackParams();

// Keycloak rejects a redirect URI that still carries the params it appended to the login callback, iss included
const currentUri = currentCleanUri();

// Initialize OIDC
const dataUserAuth = document.getElementById("app-user-auth")!.dataset;
const clientId = dataUserAuth.keycloakClient ?? "";
const authorityUrl = `${dataUserAuth.keycloakUrl ?? ""}/realms/${dataUserAuth.keycloakRealm ?? ""}`;
const sessionStorageKey = `oidc.user:${dataUserAuth.keycloakUrl ?? ""}/realms/${dataUserAuth.keycloakRealm ?? ""}:${clientId}`;

// Set up the session from dataUserAuth if present
if (dataUserAuth.session !== "") {
  localStorage.setItem(sessionStorageKey, JSON.stringify(dataUserAuth.session));
}

const oidcSettings = {
  authority: authorityUrl,
  client_id: clientId,
  redirect_uri: currentUri,
  silent_redirect_uri: currentUri,
  automaticSilentRenew: true,
  includeIdTokenInSilentRenew: true,
  userStore: new WebStorageStateStore({ store: localStorage }),
  // Token refresh settings
  accessTokenExpiringNotificationTime: 60, // Start refresh 60 seconds before expiration
  silentRequestTimeout: 10000, // 10 second timeout for silent refresh
};

const authClient = new UserManager(oidcSettings);

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null);
  const isLoggingOut = ref<boolean>(false);

  async function handleExpiredSession() {
    console.log("Handling expired session...");
    await authClient.signinRedirect();
  }

  async function loadUser() {
    const loadedUser = await authClient.getUser();
    if (loadedUser) {
      // Check if the token is expired
      const now = Math.floor(Date.now() / 1000);
      const isExpired = loadedUser.expires_at && loadedUser.expires_at <= now;
      
      if (isExpired) {
        console.log("Stored token is expired, attempting refresh...");
        try {
          // Try to refresh the token
          const refreshedUser = await authClient.signinSilent();
          if (refreshedUser) {
            user.value = refreshedUser;
            document.cookie = `KC_ACCESS_TOKEN=${refreshedUser.access_token};path=/`;
            console.log("Token refreshed successfully on page load");
          } else {
            console.log("Token refresh failed on page load, redirecting to login");
            await handleExpiredSession();
          }
        } catch (error) {
          console.error("Token refresh error on page load:", error);
          await handleExpiredSession();
        }
      } else {
        // Token is still valid
        user.value = loadedUser;
        document.cookie = `KC_ACCESS_TOKEN=${loadedUser.access_token};path=/`;
      }
    } else {
      await authClient.signinRedirect();
    }
  }

  async function authLogout(postLogoutRedirectUri = currentUri) {
    isLoggingOut.value = true;
    
    // Clear the interval to prevent interference
    if (userLoadedIntervalId) {
      clearInterval(userLoadedIntervalId);
      userLoadedIntervalId = null;
    }
    
    try {
      const idTokenHint = JSON.parse(localStorage[sessionStorageKey]).id_token;
      localStorage.removeItem(sessionStorageKey);
      user.value = null;
      
      await authClient.signoutRedirect({
        post_logout_redirect_uri: postLogoutRedirectUri,
        id_token_hint: idTokenHint,
      });
    } catch (error) {
      console.error("Error during logout:", error);
      // Even if there's an error, try to redirect to logout
      window.location.href = postLogoutRedirectUri;
    }
  }

  let userLoadedIntervalId: ReturnType<typeof setInterval> | null = null;
  // Handle user loaded events
  authClient.events.addUserLoaded(async () => {
    user.value = await authClient.getUser();
    if (user.value) {
      document.cookie = `KC_ACCESS_TOKEN=${user.value.access_token};path=/`;
    }
    // Check if the user has logged out from another tab
    if (!!userLoadedIntervalId) {
      clearInterval(userLoadedIntervalId);
    }
    userLoadedIntervalId = setInterval(async () => {
      if (isLoggingOut.value) return;
      
      try {
        const currentUser = await authClient.getUser();
        if (currentUser === null && !isLoggingOut.value) {
          console.log("User session lost, redirecting to sign in...");
          await authClient.signinRedirect();
        }
      } catch (err) {
        if (!isLoggingOut.value) {
          console.error("Error checking user session:", err);
          await authClient.signinRedirect();
        }
      }
    }, 2000);
  });

  // Handle access token expiration - try to refresh first, then logout if refresh fails
  authClient.events.addAccessTokenExpired(async () => {
    if (isLoggingOut.value) return;
    
    console.log("Access token expired, attempting silent refresh...");
    try {
      const refreshedUser = await authClient.signinSilent();
      if (refreshedUser && !isLoggingOut.value) {
        user.value = refreshedUser;
        document.cookie = `KC_ACCESS_TOKEN=${refreshedUser.access_token};path=/`;
        console.log("Token refreshed successfully");
      } else if (!isLoggingOut.value) {
        console.log("Silent refresh failed, logging out");
        await authLogout();
      }
    } catch (error) {
      if (!isLoggingOut.value) {
        console.error("Silent refresh error:", error);
        await authLogout();
      }
    }
  });

  if (shouldRegisterSigninRedirectCallback) {
    authClient.signinRedirectCallback().then((loadedUser) => {
      document.cookie = `KC_ACCESS_TOKEN=${loadedUser.access_token};path=/`;
      // Clear the keycloak args from the browser URL address
      window.history.replaceState({}, window.document.title, currentUri);
    }).catch(() => {
      authClient.signinRedirect();
    });
  } else {
    const initUser = async () => await loadUser();
    initUser();
  }

  // Reads from authClient rather than user.value, so a silent renewal is picked up before the userLoaded event writes it back
  const authFetch = createAuthFetch(async () => (await authClient.getUser())?.access_token);

  return { user, loadUser, authLogout, authClient, authFetch };
});
